// Smooth opening and closing for the whole theme: accordions (Collapsible rows incl. FAQ, mobile menu groups,
// collection filters), the footer columns on mobile (assets/footer.js), header dropdowns, drawers and modals.
// Instant when Theme settings > Animations is off (--reveal-duration 0ms) or the visitor prefers reduced motion.
//   window.theme.motion.height(el, from, to, ms, done)   animate an element's height (from/to in px)
//   window.theme.motion.details(details, open)           open or close a <details> smoothly
//   window.theme.motion.leave(el, keyframes, ms, done)    play keyframes, then done() (closing panels and dialogs)
//   window.theme.motion.closeDialog(dialog, keyframes)   animated dialog.close()
//   window.theme.motion.reopen(dialog)                   cancel a close that is still animating (opened again quickly)
(() => {
  window.theme = window.theme || {};
  if (window.theme.motion) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const rootStyle = () => getComputedStyle(document.documentElement);
  const off = () => reduce.matches || parseFloat(rootStyle().getPropertyValue('--reveal-duration')) === 0 || !Element.prototype.animate;
  const easing = () => rootStyle().getPropertyValue('--animation-easing').trim() || 'cubic-bezier(.22, 1, .36, 1)';
  const running = new WeakMap();
  const fallbacks = new WeakMap(); // a closing panel always finishes, even when the animation's finish event is late
  const ACCORDIONS = 'details.collapsible-row, details.header__drawer-group, details.cp__filter';

  const stop = (el) => {
    const animation = running.get(el);
    if (!animation) return;
    animation.onfinish = null;
    animation.cancel();
    running.delete(el);
    clearTimeout(fallbacks.get(el));
    fallbacks.delete(el);
  };

  const height = (el, from, to, ms = 300, done) => {
    stop(el);
    if (off() || Math.abs(from - to) < 1) { done?.(); return; }
    el.style.overflow = 'clip';
    const animation = el.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: ms, easing: easing() });
    running.set(el, animation);
    const end = () => { running.delete(el); el.style.overflow = ''; };
    animation.oncancel = end;
    animation.onfinish = () => { end(); done?.(); };
  };

  const leave = (el, keyframes, ms = 200, done) => {
    stop(el);
    if (off()) { done?.(); return; }
    const animation = el.animate(keyframes, { duration: ms, easing: easing() });
    running.set(el, animation);
    let finished = false;
    const finish = () => { if (finished) return; finished = true; running.delete(el); clearTimeout(fallbacks.get(el)); fallbacks.delete(el); done?.(); };
    animation.onfinish = finish;
    fallbacks.set(el, setTimeout(finish, ms + 150));
  };

  // FAQ speed setting, else 300ms.
  const speed = (details) => parseFloat(getComputedStyle(details).getPropertyValue('--faq-speed')) || 300;

  // A panel that floats (dropdown filters) is not part of the row's height: no height animation there.
  const floats = (details) => [...details.children].some((child) => child.tagName !== 'SUMMARY' && /absolute|fixed/.test(getComputedStyle(child).position));

  const closedHeight = (details) => {
    const style = getComputedStyle(details);
    const summary = details.querySelector(':scope > summary');
    if (!summary) return 0;
    return summary.getBoundingClientRect().bottom - details.getBoundingClientRect().top + parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth);
  };

  const details = (el, open) => {
    if (floats(el)) { el.open = open; return; }
    const closing = el.dataset.motionClosing === 'true';
    if (open && el.open && !closing) return;
    if (!open && (!el.open || closing)) return;
    const from = el.getBoundingClientRect().height;
    stop(el);
    if (open) {
      delete el.dataset.motionClosing;
      // One open at a time (details with the same name): the others close smoothly too. The name is held back while
      // this one opens, so the browser does not snap the others shut.
      const group = el.getAttribute('name') || el.dataset.motionName;
      if (group) {
        document.querySelectorAll(`details[name="${CSS.escape(group)}"], details[data-motion-name="${CSS.escape(group)}"]`).forEach((other) => {
          if (other.getAttribute('name')) { other.dataset.motionName = group; other.removeAttribute('name'); }
          if (other !== el && other.open) details(other, false);
        });
      }
      el.open = true;
      height(el, from, el.getBoundingClientRect().height, speed(el));
    } else {
      el.dataset.motionClosing = 'true';
      height(el, from, closedHeight(el), speed(el), () => { el.open = false; delete el.dataset.motionClosing; });
    }
  };

  const closeDialog = (dialog, keyframes, ms = 220) => {
    if (!dialog?.open || dialog.dataset.motionClosing === 'true') return;
    if (off()) { dialog.close(); return; }
    dialog.dataset.motionClosing = 'true';
    try { dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms, easing: 'ease', pseudoElement: '::backdrop' }); } catch (error) { /* no backdrop animation */ }
    leave(dialog, keyframes, ms, () => { delete dialog.dataset.motionClosing; dialog.close(); });
  };

  // Opened again while the close animation still runs: keep it open.
  const reopen = (dialog) => {
    if (dialog?.dataset.motionClosing !== 'true') return false;
    stop(dialog);
    delete dialog.dataset.motionClosing;
    return true;
  };

  // Accordions: the summary click opens / closes smoothly instead of snapping.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('summary');
    const el = summary?.parentElement;
    if (!el?.matches(ACCORDIONS) || summary !== el.querySelector(':scope > summary') || off()) return;
    const inner = event.target.closest('a, button, input, select, label');
    if (inner && summary.contains(inner)) return; // a link or button inside the summary does its own thing
    event.preventDefault();
    details(el, !el.open || el.dataset.motionClosing === 'true');
  });

  window.theme.motion = { height, details, leave, closeDialog, reopen, off };
})();
